import { useEffect, useState } from "react";
import { Loader2, Star, ArrowRight, CircleDot, Eye } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ZoomableImage } from "@/components/ZoomableImage";
import { RichText, isRichEmpty } from "@/components/RichText";
import { useAuth } from "@/context/AuthContext";

const NAVY = "#102A43";
const QUINTE = "#0C58A3";
const STRIKE = "#D2302B";
const SKY = "#A9C6E0";
const wordmark = { fontFamily: "'Rock Salt', cursive" };

function Stars({ n = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className="h-4 w-4" style={{ color: i <= n ? STRIKE : "#3a5068", fill: i <= n ? STRIKE : "transparent" }} />
      ))}
    </div>
  );
}

export default function Orbit() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: pub } = await api.get("/orbit");
        if (pub && pub.published) {
          if (alive) { setData(pub); setLoading(false); }
          return;
        }
        // Not public yet — allow admins to preview
        if (user) {
          try {
            const { data: adm } = await api.get("/admin/orbit");
            if (alive) { setData(adm); setPreview(true); setLoading(false); return; }
          } catch { /* not admin */ }
        }
        if (alive) { setData(pub || { published: false }); setLoading(false); }
      } catch {
        if (alive) { setData({ published: false }); setLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center" style={{ background: NAVY }}>
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      </Layout>
    );
  }

  if (!data?.published && !preview) {
    if (data?.coming_soon_image) {
      return (
        <Layout>
          <div className="min-h-[70vh] flex items-center justify-center px-4 py-10" style={{ background: NAVY }} data-testid="orbit-coming-soon">
            <img src={fileUrl(data.coming_soon_image)} alt="Orbit — coming soon" className="max-h-[85vh] w-auto max-w-full rounded-lg shadow-2xl" data-testid="orbit-coming-soon-image" />
          </div>
        </Layout>
      );
    }
    return (
      <Layout>
        <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6" style={{ background: NAVY, color: "white" }} data-testid="orbit-coming-soon">
          <p className="text-6xl sm:text-7xl mb-6" style={wordmark}>Orbit</p>
          <p className="uppercase tracking-[0.35em] text-sm font-bold" style={{ color: SKY }}>Precision Chalk</p>
          <h1 className="mt-8 font-heading font-extrabold text-3xl sm:text-4xl">Coming soon</h1>
          <p className="mt-3 text-gray-300 max-w-md">Something precise is on its way. Check back this September.</p>
        </div>
      </Layout>
    );
  }

  const o = data;
  const gallery = (o.gallery || []).filter((g) => g.image);
  const specs = (o.specs || []).filter((s) => (s.label || "").trim());
  const reviews = (o.reviews || []).filter((r) => (r.quote || "").trim());

  return (
    <Layout>
      {preview && (
        <div className="bg-[#D2302B] text-white text-center text-sm font-semibold py-2 px-4" data-testid="orbit-preview-banner">
          Admin preview — this page is not public yet. Flip “Publish” on in the ORBIT admin tab when you’re ready.
        </div>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: NAVY }} data-testid="orbit-hero">
        <div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle at 80% 20%, ${QUINTE}, transparent 60%)` }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div className="text-white">
            {o.logo ? (
              <img src={fileUrl(o.logo)} alt="Orbit Precision Chalk" className="h-28 sm:h-36 w-auto object-contain mb-6" data-testid="orbit-logo" />
            ) : (
              <div className="mb-6">
                <p className="text-6xl sm:text-7xl leading-none" style={wordmark} data-testid="orbit-wordmark">Orbit</p>
                <p className="mt-3 uppercase tracking-[0.35em] text-sm font-bold" style={{ color: SKY }}>{o.tagline || "Precision Chalk"}</p>
              </div>
            )}
            <p className="uppercase tracking-[0.3em] text-xs font-bold" style={{ color: SKY }}>{o.sub_tagline || "Master Every Motion"}</p>
            <h1 className="mt-4 font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-lg">
              {o.headline || "Motion, shaped into control."}
            </h1>
            {o.intro && !isRichEmpty(o.intro) && (
              <div className="mt-5 max-w-lg text-gray-200 [&_a]:text-[#A9C6E0]">
                <RichText html={o.intro} className="!text-gray-200" testid="orbit-intro" />
              </div>
            )}
            {o.cta_url && (
              <a href={o.cta_url} target="_blank" rel="noopener noreferrer" data-testid="orbit-cta"
                className="mt-7 inline-flex items-center gap-2 rounded-md px-6 py-3 font-bold text-white transition-transform hover:scale-[1.03]" style={{ background: STRIKE }}>
                {o.cta_label || "Learn more"} <ArrowRight className="h-4 w-4" />
              </a>
            )}
          </div>
          <div className="flex justify-center">
            {o.hero_image ? (
              <ZoomableImage src={fileUrl(o.hero_image)} alt="Orbit Precision Chalk" className="w-full max-w-md rounded-xl shadow-2xl object-contain" testId="orbit-hero-image" />
            ) : (
              <div className="w-full max-w-md aspect-square rounded-xl border-2 border-dashed flex items-center justify-center" style={{ borderColor: "#2c4763", color: SKY }}>
                <CircleDot className="h-16 w-16" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Specs */}
      {specs.length > 0 && (
        <section className="bg-white" data-testid="orbit-specs">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <h2 className="font-heading font-extrabold text-2xl mb-6" style={{ color: NAVY }}>Specifications</h2>
            <div className="grid sm:grid-cols-2 gap-px rounded-xl overflow-hidden border" style={{ borderColor: SKY }}>
              {specs.map((s) => (
                <div key={s.id} className="bg-[#F7FAFD] p-4 flex justify-between gap-4" data-testid={`orbit-spec-${s.id}`}>
                  <span className="text-sm font-semibold" style={{ color: QUINTE }}>{s.label}</span>
                  <span className="text-sm text-[#333] text-right">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section style={{ background: "#F2F6FB" }} data-testid="orbit-gallery">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <h2 className="font-heading font-extrabold text-2xl mb-6" style={{ color: NAVY }}>Gallery</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {gallery.map((g) => (
                <figure key={g.id} className="bg-white rounded-xl overflow-hidden shadow-sm border" style={{ borderColor: "#E3ECF5" }} data-testid={`orbit-gallery-${g.id}`}>
                  <ZoomableImage src={fileUrl(g.image)} alt={g.caption || "Orbit"} className="w-full h-56 object-cover" testId={`orbit-gallery-img-${g.id}`} />
                  {g.caption && <figcaption className="p-3 text-sm text-[#333]">{g.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <section style={{ background: NAVY }} data-testid="orbit-reviews">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <h2 className="font-heading font-extrabold text-2xl mb-6 text-white">What players are saying</h2>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {reviews.map((r) => (
                <blockquote key={r.id} className="rounded-xl p-6" style={{ background: "#173650" }} data-testid={`orbit-review-${r.id}`}>
                  {r.rating ? <Stars n={Number(r.rating)} /> : null}
                  <div className="mt-3 text-gray-100 [&_p]:my-1"><RichText html={r.quote} className="!text-gray-100" /></div>
                  <footer className="mt-4 text-sm font-semibold" style={{ color: SKY }}>
                    {r.author || "Anonymous"}{r.location ? ` · ${r.location}` : ""}
                  </footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
